import { clsx } from 'clsx';
import { useState, useEffect, forwardRef, Suspense, lazy } from 'react';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

const unknownFlagName = 'wise';
const Flag = ({
  code,
  intrinsicSize = 64,
  className = undefined,
  loading = 'lazy'
}) => {
  const [fallback, setFallback] = useState(null);
  useEffect(() => {
    setFallback(null);
  }, [code]);
  const detailed = intrinsicSize >= 150;
  const name = fallback !== 'unknown' ? `${code.toLowerCase()}${fallback == null && detailed ? '-detailed' : ''}` : unknownFlagName;
  return /*#__PURE__*/jsx("img", {
    className: clsx(`wds-flag wds-flag-${name}`, className),
    src: process.env.NODE_ENV === 'web-art-dev' ? `${name}.svg` : `https://wise.com/web-art/assets/flags/${name}.svg`,
    loading: loading,
    alt: "",
    width: intrinsicSize,
    height: intrinsicSize,
    onError: () => {
      setFallback(prev => prev == null && detailed ? 'simple' : 'unknown');
    }
  });
};

var Sizes;
(function (Sizes) {
  Sizes["SMALL"] = "small";
  Sizes["MEDIUM"] = "medium";
  Sizes["LARGE"] = "large";
})(Sizes || (Sizes = {}));
const ImageSizes = {
  [Sizes.SMALL]: 200,
  [Sizes.MEDIUM]: 300,
  [Sizes.LARGE]: 500
};

const imageSizes = {
  [Sizes.SMALL]: 200,
  [Sizes.MEDIUM]: 300,
  [Sizes.LARGE]: 500
};
const Illustration = /*#__PURE__*/forwardRef(({
  id,
  name,
  alt = '',
  loading = 'eager',
  className,
  size = Sizes.MEDIUM,
  disablePadding = false
}, ref) => {
  const {
    SMALL,
    MEDIUM
  } = Sizes;
  return name ? /*#__PURE__*/jsxs("picture", {
    children: [size === Sizes.LARGE || size === Sizes.MEDIUM ? /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx("source", {
        width: imageSizes[SMALL],
        height: imageSizes[SMALL],
        media: "(max-width: 575px)",
        srcSet: `${defineSrc$1(name, SMALL, '1x')}, ${defineSrc$1(name, SMALL, '2x')} 2x`
      }), size !== Sizes.MEDIUM ? /*#__PURE__*/jsx("source", {
        width: imageSizes[MEDIUM],
        height: imageSizes[MEDIUM],
        media: "(max-width: 992px)",
        srcSet: `${defineSrc$1(name, MEDIUM, '1x')}, ${defineSrc$1(name, MEDIUM, '2x')} 2x`
      }) : null]
    }) : null, /*#__PURE__*/jsx("img", {
      id: id,
      ref: ref,
      alt: alt,
      "data-testid": `wds-${name}-illustration`,
      className: clsx(`wds-illustration wds-illustration-${name}`, className, !disablePadding && 'wds-illustration-padding'),
      loading: loading,
      src: defineSrc$1(name, size, '1x'),
      srcSet: `${defineSrc$1(name, size, '2x')} 2x`,
      width: imageSizes[size],
      height: imageSizes[size]
    })]
  }) : null;
});
function defineSrc$1(illustration, size, descriptor) {
  return `https://wise.com/web-art/assets/illustrations/${illustration}-${size}@${descriptor}.webp`;
}

/*
  *
  * DON'T MODIFY THIS FILE IT'S AUTO GENERATED
  * See: `scripts/generate-i10s-metadata.mjs`
  *
  */
var Assets;
(function (Assets) {
  Assets["WALLET"] = "wallet";
  Assets["TWO_INVITE_LETTERS_OPENED"] = "two-invite-letters-opened";
  Assets["TOOL"] = "tool";
  Assets["TARGET_PLATFORM"] = "target-platform";
  Assets["STOPWATCH_PLATFORM"] = "stopwatch-platform";
  Assets["SPEECH_BUBBLE"] = "speech-bubble";
  Assets["SKIP_AUTHENTICATION"] = "skip-authentication";
  Assets["SHOPPING_BAG"] = "shopping-bag";
  Assets["SATELLITE_PLATFORM"] = "satellite-platform";
  Assets["SAND_TIMER"] = "sand-timer";
  Assets["REMINDER_LETTER"] = "reminder-letter";
  Assets["RECEIVE"] = "receive";
  Assets["QUESTION_MARK"] = "question-mark";
  Assets["PUZZLE_PIECES"] = "puzzle-pieces";
  Assets["PLANE_2"] = "plane-2";
  Assets["PLANE"] = "plane";
  Assets["PIE_CHART"] = "pie-chart";
  Assets["PHONES"] = "phones";
  Assets["PERSONAL_CARD"] = "personal-card";
  Assets["PERCENTAGE"] = "percentage";
  Assets["PALM_TREE"] = "palm-tree";
  Assets["ONE_INVITE_LETTER_OPENED"] = "one-invite-letter-opened";
  Assets["MULTI_CURRENCY"] = "multi-currency";
  Assets["MEGAPHONE"] = "megaphone";
  Assets["MARBLE_CARD_FLAG_ONLY"] = "marble-card-flag-only";
  Assets["MARBLE_CARD_BUSINESS_FLAG_ONLY"] = "marble-card-business-flag-only";
  Assets["MARBLE_CARD_BUSINESS"] = "marble-card-business";
  Assets["MARBLE_CARD"] = "marble-card";
  Assets["MARBLE"] = "marble";
  Assets["MAP"] = "map";
  Assets["MAGNIFYING_GLASS"] = "magnifying-glass";
  Assets["LOCK_PLATFORM"] = "lock-platform";
  Assets["LOCK"] = "lock";
  Assets["LIGHT_BULB"] = "light-bulb";
  Assets["KEY"] = "key";
  Assets["JARS"] = "jars";
  Assets["INVITE_LETTER"] = "invite-letter";
  Assets["INTEREST"] = "interest";
  Assets["INFINITE"] = "infinite";
  Assets["ID_CARD"] = "id-card";
  Assets["HOUSE"] = "house";
  Assets["HEART_5"] = "heart-5";
  Assets["HEART_4"] = "heart-4";
  Assets["HEART_3"] = "heart-3";
  Assets["HEART_2"] = "heart-2";
  Assets["HEART"] = "heart";
  Assets["GRAPH"] = "graph";
  Assets["GLOBE"] = "globe";
  Assets["GEAR"] = "gear";
  Assets["FLOWER"] = "flower";
  Assets["FLAG"] = "flag";
  Assets["EXCLAMATION_MARK"] = "exclamation-mark";
  Assets["EMAIL_SUCCESS"] = "email-success";
  Assets["EMAIL"] = "email";
  Assets["ELECTRIC_PLUG"] = "electric-plug";
  Assets["ECO_CARD"] = "eco-card";
  Assets["DOOR"] = "door";
  Assets["DOCUMENTS"] = "documents";
  Assets["DIGITAL_CARD_2"] = "digital-card-2";
  Assets["DIGITAL_CARD"] = "digital-card";
  Assets["COOKIE"] = "cookie";
  Assets["CONVERT"] = "convert";
  Assets["CONSTRUCTION_FENCE"] = "construction-fence";
  Assets["CONFETTI"] = "confetti";
  Assets["COIN_PILE_UP"] = "coin-pile-up";
  Assets["COIN_PILE_DOWN"] = "coin-pile-down";
  Assets["CLOSED_WINDOW"] = "closed-window";
  Assets["CHECK_MARK"] = "check-mark";
  Assets["CALENDAR"] = "calendar";
  Assets["BUSINESS_CARD"] = "business-card";
  Assets["BRIEFCASE"] = "briefcase";
  Assets["BELL"] = "bell";
})(Assets || (Assets = {}));

function useBattery() {
  const [batteryData, setBatteryData] = useState({
    level: 1.0
  });
  useEffect(() => {
    const getBatteryData = async () => {
      // @ts-expect-error .getBattery exists in Navigator interface
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      const battery = await navigator.getBattery();
      setBatteryData({
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        level: battery?.level
      });
    };
    // Check if the browser supports the Battery API
    if ('getBattery' in navigator && navigator.getBattery !== undefined && typeof navigator.getBattery === 'function') {
      void getBatteryData();
    }
  }, []);
  return batteryData;
}
function isBatteryLow(batteryLevel) {
  return batteryLevel < 0.2;
}
function isConnectionSlow() {
  // @ts-expect-error .connection prop exists in Navigator interface
  const {
    connection
  } = window.navigator;
  if (connection === undefined) {
    return false;
  }
  return (
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    ['slow-2g', '2g'].includes(connection?.effectiveType) ||
    // @ts-expect-error Property 'connection' does exist on type 'Navigator'
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    navigator?.connection?.saveData === true
  );
}
function defineSrc(asset) {
  return `https://wise.com/web-art/assets/illustrations3d/${asset}`;
}
const illustration3DNames = ['lock', 'globe', 'confetti', 'check-mark', 'flower', 'graph', 'jars', 'magnifying-glass', 'marble', 'marble-card', 'multi-currency', 'plane'];
function isIllustrationSupport3D(asset) {
  return illustration3DNames.includes(asset);
}

/* eslint-disable fp/no-mutation */
var RenderMode;
(function (RenderMode) {
  RenderMode[RenderMode["INIT"] = 0] = "INIT";
  RenderMode[RenderMode["FALLBACK"] = 1] = "FALLBACK";
  RenderMode[RenderMode["ASSET_3D"] = 2] = "ASSET_3D";
})(RenderMode || (RenderMode = {}));
const Illustration3D = ({
  name,
  size = Sizes.MEDIUM,
  className
}) => {
  const [renderMode, setRenderMode] = useState(RenderMode.INIT);
  const [inintrinsicSize, setInintrinsicSize] = useState(size);
  const batteryData = useBattery();
  useEffect(() => {
    setRenderMode(isConnectionSlow() || isBatteryLow(batteryData.level) ? RenderMode.FALLBACK : RenderMode.ASSET_3D);
    const isMobile = (typeof window !== undefined && window?.matchMedia('(max-width: 575px)')?.matches) ?? false;
    if (isMobile) {
      setInintrinsicSize(Sizes.SMALL);
    }
  }, [batteryData]);
  return /*#__PURE__*/jsx("div", {
    className: clsx(`wds-illustration-3d wds-illustration-3d-${name} wds-illustration-3d-${inintrinsicSize}`, className),
    "aria-hidden": true,
    children: renderMode === RenderMode.INIT ? null : renderMode === RenderMode.ASSET_3D ? /*#__PURE__*/jsx(Suspense, {
      fallback: null,
      children: getModel({
        name,
        size: inintrinsicSize
      })
    }) : /*#__PURE__*/jsx(Illustration, {
      name: name,
      size: size,
      alt: ""
    })
  });
};
function getModel({
  name,
  size
}) {
  // @ts-expect-error unknown generic
  let Model = /*#__PURE__*/jsx(Fragment, {});
  const Scene = /*#__PURE__*/lazy(() => import('./Scene-hCN3uBwY.esm.js'));
  switch (name) {
    case Assets.LOCK:
      {
        Model = /*#__PURE__*/lazy(() => import('./Lock-Bw1zz33w.esm.js'));
        break;
      }
    case Assets.GLOBE:
      {
        Model = /*#__PURE__*/lazy(() => import('./Globe-BcSKBv6m.esm.js'));
        break;
      }
    case Assets.CONFETTI:
      {
        Model = /*#__PURE__*/lazy(() => import('./Confetti-Bo2BAve7.esm.js'));
        break;
      }
    case Assets.CHECK_MARK:
      {
        Model = /*#__PURE__*/lazy(() => import('./CheckMark-CHI3WOfw.esm.js'));
        break;
      }
    case Assets.FLOWER:
      {
        Model = /*#__PURE__*/lazy(() => import('./Flower-B3qB1c4O.esm.js'));
        break;
      }
    case Assets.PLANE:
      {
        Model = /*#__PURE__*/lazy(() => import('./Plane-rLKcQRQ9.esm.js'));
        break;
      }
    case Assets.GRAPH:
      {
        Model = /*#__PURE__*/lazy(() => import('./Graph-DDO1Xov2.esm.js'));
        break;
      }
    case Assets.MARBLE:
      {
        Model = /*#__PURE__*/lazy(() => import('./Marble-BQXbhXNM.esm.js'));
        break;
      }
    case Assets.MARBLE_CARD:
      {
        Model = /*#__PURE__*/lazy(() => import('./MarbleCard-_rlYsi-c.esm.js'));
        break;
      }
    case Assets.MAGNIFYING_GLASS:
      {
        Model = /*#__PURE__*/lazy(() => import('./MagnifyingGlass-B-h_pSgp.esm.js'));
        break;
      }
    case Assets.JARS:
      {
        Model = /*#__PURE__*/lazy(() => import('./Jars-DN6mmpjY.esm.js'));
        break;
      }
    case Assets.MULTI_CURRENCY:
      {
        Model = /*#__PURE__*/lazy(() => import('./MultiCurrency-BlFHk4Zk.esm.js'));
        break;
      }
  }
  return /*#__PURE__*/jsx(Scene, {
    assetName: name,
    size: size,
    children: /*#__PURE__*/jsx(Model, {})
  });
}

export { Assets as A, Flag as F, ImageSizes as I, Sizes as S, Illustration as a, Illustration3D as b, defineSrc as d, isIllustrationSupport3D as i };
//# sourceMappingURL=index-ClPpN6dk.esm.js.map
